import React from 'react';
import { Link } from 'react-router-dom';
import { FilePreviewCard, ImagePreview, Pagination } from '@/components';
import {
  BUSINESS_UNIT_LABEL,
  COMPANY_ENTITY_LABEL,
  PurchaseOrderDetail,
  PurchaseReceiveItem,
} from '@/types/purchase-order';

const isImageUrl = (url: string) =>
  /\.(png|jpe?g|webp|gif)$/i.test(url.split('?')[0]);

const fileNameFromUrl = (url: string) => {
  try {
    const path = url.split('?')[0];
    const seg = path.substring(path.lastIndexOf('/') + 1);
    return decodeURIComponent(seg) || 'attachment';
  } catch {
    return 'attachment';
  }
};

interface PurchaseOrderDetailContentProps {
  purchaseOrder: PurchaseOrderDetail;
  oemName?: string;
  modelName?: string;
  receiveItems: PurchaseReceiveItem[];
  receivedTotal: number;
  receivePage: number;
  receivePageSize: number;
  receiveLoading?: boolean;
  onReceivePageChange: (page: number) => void;
  onReceivePageSizeChange: (size: number) => void;
}

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(parsed)
    .replace(/\./g, ':');
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-2">
    <div className="text-gray-500">{label}</div>
    <div className="text-right font-medium text-gray-900 break-words">
      {children}
    </div>
  </div>
);

const PurchaseOrderDetailContent: React.FC<PurchaseOrderDetailContentProps> = ({
  purchaseOrder,
  oemName,
  modelName,
  receiveItems,
  receivedTotal,
  receivePage,
  receivePageSize,
  receiveLoading = false,
  onReceivePageChange,
  onReceivePageSizeChange,
}) => {
  return (
    <div className="w-full space-y-6 lg:flex-1">
      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-file-list-3-line text-base" />
          </span>
          Purchase Order
        </div>

        <div className="space-y-3 text-sm">
          <Row label="Purchase Order ID">
            {displayValue(purchaseOrder.code)}
          </Row>
          <Row label="OEM">{displayValue(oemName)}</Row>
          <Row label="Model">{displayValue(modelName)}</Row>
          <Row label="Total Units">{displayValue(purchaseOrder.totalUnits)}</Row>
          <Row label="Entity">
            {(purchaseOrder.entity &&
              COMPANY_ENTITY_LABEL[purchaseOrder.entity]) ||
              displayValue(purchaseOrder.entity)}
          </Row>
          <Row label="Business Unit">
            {BUSINESS_UNIT_LABEL[purchaseOrder.requestingBusinessUnit] ||
              displayValue(purchaseOrder.requestingBusinessUnit)}
          </Row>
          <Row label="Client">{displayValue(purchaseOrder.clientName)}</Row>
          <Row label="Received Units">
            {receivedTotal} / {purchaseOrder.totalUnits}
          </Row>
        </div>
      </div>

      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-file-text-line text-base" />
          </span>
          Description &amp; Notes
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <div className="text-gray-500 mb-1">Description / Justification</div>
            <div className="font-medium text-gray-900 whitespace-pre-line break-words">
              {displayValue(purchaseOrder.description)}
            </div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Notes</div>
            <div className="font-medium text-gray-900 whitespace-pre-line break-words">
              {displayValue(purchaseOrder.notes)}
            </div>
          </div>
          {purchaseOrder.rejectionReason && (
            <div>
              <div className="text-gray-500 mb-1">Rejection Reason</div>
              <div className="font-medium text-red-600 whitespace-pre-line break-words">
                {purchaseOrder.rejectionReason}
              </div>
            </div>
          )}
        </div>
      </div>

      {purchaseOrder.demandEvidenceFileUrl && (
        <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
          <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
            <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
              <i className="ri-attachment-2 text-base" />
            </span>
            Demand Evidence
          </div>

          {isImageUrl(purchaseOrder.demandEvidenceFileUrl) ? (
            <ImagePreview
              src={purchaseOrder.demandEvidenceFileUrl}
              alt="Demand evidence"
              className="h-48 w-full rounded-md bg-gray-50 object-contain"
            />
          ) : (
            <button
              type="button"
              onClick={() =>
                window.open(
                  purchaseOrder.demandEvidenceFileUrl!,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              className="w-full text-left"
            >
              <FilePreviewCard
                fileName={fileNameFromUrl(purchaseOrder.demandEvidenceFileUrl)}
                showStatus={false}
              />
            </button>
          )}
        </div>
      )}

      <div className="border border-gray-200 px-5 py-4 shadow-sm rounded-lg">
        <div className="mb-3 flex items-center gap-2 text-[16px] font-medium text-gray-800">
          <span className="ml-[-5px] inline-flex h-7 w-7 items-center justify-center text-gray-600">
            <i className="ri-inbox-archive-line text-base" />
          </span>
          Received Units ({receivedTotal})
        </div>

        {receiveLoading ? (
          <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
            Loading received units...
          </div>
        ) : receiveItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
            No units received yet.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">
                      VIN
                    </th>
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">
                      Plat Nomor
                    </th>
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">
                      IMEI
                    </th>
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">
                      Hub Locations
                    </th>
                    <th className="py-2 pr-3 font-medium whitespace-nowrap">
                      Received At
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {receiveItems.map((item) => (
                    <tr
                      key={item.ID}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="py-2 pr-3 text-gray-900">
                        {displayValue(item.vin)}
                      </td>
                      <td className="py-2 pr-3 text-gray-900">
                        {/* Linked once the receive created a fleet unit. Plain
                            text means this row was received before units were
                            created automatically and never matched a unit. */}
                        {item.vehicleID ? (
                          <Link
                            to={`/vehicles/${item.vehicleID}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {displayValue(item.platNomor)}
                          </Link>
                        ) : (
                          displayValue(item.platNomor)
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-900">
                        {displayValue(item.imei)}
                      </td>
                      <td className="py-2 pr-3 text-gray-700">
                        {item.hubName || '-'}
                      </td>
                      <td className="py-2 pr-3 text-gray-700">
                        {formatDateTime(item.receivedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={receivePage}
              totalItems={receivedTotal}
              itemsPerPage={receivePageSize}
              onPageChange={onReceivePageChange}
              onPageSizeChange={onReceivePageSizeChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderDetailContent;
